import { useState } from 'react';
import { useRouter } from 'next/navigation';
import { useQueryClient } from '@tanstack/react-query';
import toast from 'react-hot-toast';
import { authApi } from '../api';
import { ROUTES } from '../constants';

interface FormState {
  name: string;
  email: string;
  password: string;
}

type Mode = 'login' | 'register';

const INITIAL_FORM: FormState = { name: '', email: '', password: '' };

export function useAuth(mode: Mode) {
  const router = useRouter();
  const queryClient = useQueryClient();
  const [loading, setLoading] = useState(false);
  const [form, setForm] = useState<FormState>(INITIAL_FORM);
  const [pendingVerification, setPendingVerification] = useState(false);
  const [registeredEmail, setRegisteredEmail] = useState('');

  const setField = (field: keyof FormState) => (e: React.ChangeEvent<HTMLInputElement>) =>
    setForm((prev) => ({ ...prev, [field]: e.target.value }));

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    try {
      if (mode === 'register') {
        const data = await authApi.register({ email: form.email, password: form.password, name: form.name });
        // Registration no longer issues a cookie — show the verify-email prompt
        setRegisteredEmail(data.email);
        setPendingVerification(true);
        setForm(INITIAL_FORM);
        return;
      }

      const data = await authApi.login({ email: form.email, password: form.password });

      // Seed the cache immediately so the sidebar shows the correct name
      // without waiting for a /auth/me round-trip after navigation.
      queryClient.setQueryData(['auth-me'], data.user);

      if (!data.workspaceId) {
        // Logged in but no workspace found — send to workspaces list to recover
        router.push(ROUTES.workspaces);
        return;
      }

      document.cookie = `workspaceId=${data.workspaceId}; path=/; SameSite=Lax`;
      if (!data.onboarded) {
        router.push(ROUTES.onboarding(data.workspaceId));
      } else {
        router.push(ROUTES.dashboard(data.workspaceId));
      }
    } catch (err: any) {
      console.error(`[useAuth] ${mode} failed:`, err);
      const message = err?.response?.data?.message ?? 'Something went wrong';
      toast.error(Array.isArray(message) ? message.join(', ') : message);
    } finally {
      setLoading(false);
    }
  };

  return { loading, form, setField, handleSubmit, pendingVerification, registeredEmail };
}
